import { useEffect, useRef, useState, type ReactNode } from "react";
import { AlertTriangle, Check, CircleDot, Loader2, X } from "@fg/icons";
import type { SaveStatus } from "@/lib/useSaveTracker";

// Document name, Drive location and save state for the editor title region
// of the suite top bar (SuiteAppBar `title`). The bar can be Accent, Light or
// Dark, so everything here inherits the bar's colour (currentColor, opacity)
// and uses the `.suite-appbar-*` classes — never hard-coded white.
// Click the name to rename it inline: Enter / blur commits, Esc cancels.

interface FileTitleProps {
    name: string;
    status: SaveStatus;
    /** Bound to a Drive file — a clean document then reads "Saved". */
    inDrive: boolean;
    loading: boolean;
    /** Rejects with a user-facing message (e.g. Drive's duplicate-name error). */
    onRename: (next: string) => Promise<void>;
    onRetry: () => void;
    /** Under the name — the Drive location breadcrumb. */
    location?: ReactNode;
    /** Beside the name block — version history, comments. */
    accessory?: ReactNode;
}

export function FileTitle({ name, status, inDrive, loading, onRename, onRetry, location, accessory }: FileTitleProps) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(name);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    // Enter commits and then unmounts the input, which can also fire blur.
    const settledRef = useRef(false);

    useEffect(() => {
        if (!editing) return;
        const input = inputRef.current;
        if (!input) return;
        input.focus();
        // Select the base name only, like Explorer / OneDrive.
        const dot = input.value.lastIndexOf(".");
        input.setSelectionRange(0, dot > 0 ? dot : input.value.length);
    }, [editing]);

    const begin = () => {
        settledRef.current = false;
        setDraft(name);
        setError(null);
        setEditing(true);
    };

    const cancel = () => {
        settledRef.current = true;
        setEditing(false);
        setError(null);
    };

    const commit = async () => {
        if (settledRef.current || busy) return;
        const next = draft.trim();
        if (!next || next === name) {
            cancel();
            return;
        }
        setBusy(true);
        try {
            await onRename(next);
            settledRef.current = true;
            setEditing(false);
            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex min-w-0 items-center gap-1">
            <div className="flex min-w-0 flex-col justify-center">
                {editing ? (
                    <input
                        ref={inputRef}
                        value={draft}
                        disabled={busy}
                        aria-label="File name"
                        aria-invalid={error ? true : undefined}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") { e.preventDefault(); void commit(); }
                            else if (e.key === "Escape") { e.preventDefault(); cancel(); }
                        }}
                        onBlur={() => void commit()}
                        className="h-6 w-64 max-w-[40vw] rounded border border-input bg-background px-1.5 text-sm font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-70"
                    />
                ) : (
                    <button
                        type="button"
                        onClick={begin}
                        title="Rename"
                        className="suite-appbar-btn h-6 min-w-0 max-w-[40vw] truncate rounded px-1.5 text-left text-sm font-semibold"
                    >
                        {name}
                    </button>
                )}
                <div className="flex min-w-0 items-center gap-2.5 px-1.5 text-xs leading-4">
                    {location ? <div className="flex min-w-0 opacity-80">{location}</div> : null}
                    {error ? (
                        <span role="alert" className="flex min-w-0 items-center gap-1 font-semibold">
                            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                            <span className="truncate">{error}</span>
                        </span>
                    ) : (
                        <span aria-live="polite" className="flex shrink-0 items-center">
                            <SaveStatusBadge status={status} inDrive={inDrive} loading={loading} onRetry={onRetry} />
                        </span>
                    )}
                </div>
            </div>
            {accessory}
        </div>
    );
}

/**
 * The top bar's Close (the last editor control, after Share): closes the file
 * and returns to the app's home. `busy` while a save in flight finishes.
 * Follows the bar's colours (Accent / Light / Dark) via suite-appbar-*.
 */
export function CloseFileButton({ appName, busy, onClose }: { appName: string; busy: boolean; onClose: () => void }) {
    return (
        <div className="suite-appbar-divider flex shrink-0 items-center pl-1.5">
            <button
                type="button"
                onClick={onClose}
                disabled={busy}
                aria-label="Close file"
                aria-busy={busy || undefined}
                title={busy ? "Finishing the save before closing…" : `Close file (return to ${appName} home)`}
                className="suite-appbar-btn flex h-8 w-8 items-center justify-center rounded disabled:cursor-wait"
            >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <X className="h-4 w-4" aria-hidden />}
            </button>
        </div>
    );
}

function SaveStatusBadge({
    status, inDrive, loading, onRetry
}: {
    status: SaveStatus;
    inDrive: boolean;
    loading: boolean;
    onRetry: () => void;
}) {
    const muted = "flex items-center gap-1 whitespace-nowrap opacity-80";
    if (loading) {
        return <span className={muted}><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />Opening…</span>;
    }
    switch (status) {
        case "saving":
            return <span className={muted}><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />Saving…</span>;
        case "failed":
            return (
                <button
                    type="button"
                    onClick={onRetry}
                    className="suite-appbar-btn flex items-center gap-1 whitespace-nowrap rounded px-1 font-semibold underline-offset-2 hover:underline"
                >
                    <AlertTriangle className="h-3.5 w-3.5" aria-hidden />Save failed — retry
                </button>
            );
        case "dirty":
            return <span className={muted}><CircleDot className="h-3.5 w-3.5" aria-hidden />Unsaved changes</span>;
        default:
            return inDrive
                ? <span className={muted} title="Saved to Drive"><Check className="h-3.5 w-3.5" aria-hidden />Saved</span>
                : null;
    }
}
